import {Database, DataSnapshot, get, getDatabase, limitToFirst, orderByKey, query, ref, set, startAfter}
    from "@firebase/database";
import {ProjectTables} from "../components/projects/Projects";

export interface IRecordFingerprint {
    hash: number,
    size: number
}
export interface ITableFingerprint {
    hash: number
    records: {[key: string]: IRecordFingerprint}
    size: number
}
export interface IDatabaseFingerprint {
    hash: number
    tables: {[key: string]: ITableFingerprint}
    size: number
}
export type TProgress = (label: string, progress: number) => void
export class FirebaseTools {
    private static ImportTables = [
        "configs/<project>",
        "constants/<project>",
        "localization/<project>",
        "networks/<project>",
        "nodes/<project>",
        "projects/<project>",
        "sheets/<project>",
        "tags/<project>",
        "variables/<project>"
    ];
    private static maxChunkSize = 10000;

    static GetHashNumbers(source: number, value: number) {
        let hash = (source << 5) - source + value;

        return hash |= 0;
    }
    static GetHash(source: string) {
        let hash = 0;

        for (let character = 0; character < source.length; ++character) {
            hash = this.GetHashNumbers(hash, source.charCodeAt(character));
        }
        return hash;
    }

    private static UpdateFingerprint(fingerprint: ITableFingerprint, key: string, json: string) {
        const hash = this.GetHash(json);
        const size = json.length;

        fingerprint.hash = this.GetHashNumbers(fingerprint.hash, hash);
        fingerprint.size += size;
        fingerprint.records[key] = {hash, size};
    }

    static async ImportFirebase(databaseSource: Database, project: string, progress?: TProgress) {
        if (databaseSource) {
            const importTable = async (path: string) => {
                const fingerprint: ITableFingerprint = {
                    hash: 0,
                    records: {},
                    size: 0
                };
                let start = "";
                let chunkSize = 8;
                let snapshot: DataSnapshot;

                do {
                    snapshot = await get(query(ref(databaseSource, path), orderByKey(),
                        startAfter(start), limitToFirst(chunkSize)));
                    if (snapshot.val()) {
                        const data = snapshot.val();
                        let size = 0;

                        for (let key of Object.keys(data)) {
                            const json = JSON.stringify(data[key]);

                            size += json.length;
                            start = key;
                            this.UpdateFingerprint(fingerprint, key, json);
                            await set(ref(getDatabase(), `${path}/${key}`), data[key]);
                        }
                        if (size < this.maxChunkSize) {
                            chunkSize *= 2;
                        }
                    }
                } while (snapshot.val() !== null);
                return fingerprint;
            };
            const fingerprint: IDatabaseFingerprint = {
                hash: 0,
                tables: {},
                size: 0
            };
            let imported = 0;

            for (let tableSource of this.ImportTables) {
                const source = tableSource.replace("<project>", project);
                const table = source.split('/').shift() as string;

                progress && progress(source, Math.floor(100 * ++imported / ProjectTables.length));
                fingerprint.tables[table] = await importTable(source);
                fingerprint.hash = this.GetHashNumbers(fingerprint.hash, fingerprint.tables[table].hash)
                fingerprint.size += fingerprint.tables[table].size;
            }
            progress && progress("Complete", 100);
            return fingerprint;
        }
    }
    
    static async GetDatabaseFingerprint(project: string, progress?: TProgress) {
        const database = getDatabase();
        const readTableFingerprint = async (path: string): Promise<ITableFingerprint> => {
            const fingerprint: ITableFingerprint = {
                hash: 0,
                records: {},
                size: 0
            };
            let start = "";
            let chunkSize = 8;
            let snapshot: DataSnapshot;

            do {
                snapshot = await get(query(ref(database, path), orderByKey(),
                    startAfter(start), limitToFirst(chunkSize)));
                if (snapshot.val()) {
                    const data = snapshot.val();
                    let size = 0;

                    for (let key of Object.keys(data)) {
                        const json = JSON.stringify(data[key]);

                        start = key;
                        this.UpdateFingerprint(fingerprint, key, json);
                    }
                    if (size < this.maxChunkSize) {
                        chunkSize *= 2;
                    }
                }
            } while (snapshot.val() !== null);
            return fingerprint;
        };
        let imported = 0;
        const fingerprint: IDatabaseFingerprint = {
            hash: 0,
            tables: {},
            size: 0
        };

        for (let importTable of this.ImportTables) {
            const source = importTable.replace("<project>", project);
            const table = importTable.split('/').shift() as string;

            progress && progress(source, Math.floor(100 * ++imported / ProjectTables.length));

            fingerprint.tables[table] = await readTableFingerprint(source);
            fingerprint.hash = this.GetHashNumbers(fingerprint.hash, fingerprint.tables[table].hash)
            fingerprint.size += fingerprint.tables[table].size;
        }
        progress && progress("Complete", 100);
        return fingerprint;
    }
}